import { useAuthStore } from "@/stores/useAuthStore";

export const ROLE = {
  SCHEDULING_SUPERVISOR: "scheduling_supervisor",
  SUPPORT_SCHEDULING_SUPERVISOR: "support_scheduling_supervisor",
  SUPPORT_VIEWER: "support_viewer",
  RAD_ADMIN: "rad_admin",
  RADIOLOGIST: "radiologist",
  ADMIN: "admin",
  SUPPORT: "support",
  SUPPORT_ADMIN: "support_admin",
  GOD: "god",
} as const;

export type Role = (typeof ROLE)[keyof typeof ROLE];

export type Permission =
  | "view_schedule"
  | "edit_schedule"
  | "print_schedule"
  | "view_forecast"
  | "view_analytics"
  | "analytics_reports"
  | "analytics_admin"
  | "view_roster"
  | "manage_settings"
  | "manage_editor_config"
  | "manage_users"
  | "manage_studies"
  | "manage_vacations"
  | "view_support_schedule"
  | "manage_support_schedule"
  | "manage_rad_support"
  | "access_intake"
  | "manage_onboarding_invites";

const ROLE_PERMISSIONS: Record<Role, ReadonlySet<Permission>> = {
  [ROLE.SCHEDULING_SUPERVISOR]: new Set([
    "view_schedule",
    "edit_schedule",
    "print_schedule",
    "view_forecast",
    "view_analytics",
    "analytics_reports",
    "view_roster",
    "manage_settings",
    "manage_editor_config",
    "manage_users",
    "manage_studies",
    "manage_vacations",
    "view_support_schedule",
  ]),
  [ROLE.SUPPORT_SCHEDULING_SUPERVISOR]: new Set([
    "view_support_schedule",
    "manage_support_schedule",
  ]),
  // Read-only access to the support calendar and nothing else.
  [ROLE.SUPPORT_VIEWER]: new Set(["view_support_schedule"]),
  [ROLE.RAD_ADMIN]: new Set([
    "view_schedule",
    "print_schedule",
    "view_forecast",
    "view_analytics",
    "analytics_reports",
    "view_roster",
    "view_support_schedule",
  ]),
  [ROLE.RADIOLOGIST]: new Set([
    "view_schedule",
    "view_support_schedule",
    "access_intake",
  ]),
  [ROLE.ADMIN]: new Set([
    "view_schedule",
    "print_schedule",
    "view_analytics",
    "analytics_reports",
    "view_roster",
    "access_intake",
    "manage_vacations",
    "manage_onboarding_invites",
    "view_support_schedule",
  ]),
  [ROLE.SUPPORT]: new Set(["view_schedule", "view_roster", "view_support_schedule"]),
  [ROLE.SUPPORT_ADMIN]: new Set([
    "view_schedule",
    "view_roster",
    "manage_settings",
    "manage_users",
    "manage_vacations",
    "view_support_schedule",
    "manage_rad_support",
  ]),
  [ROLE.GOD]: new Set(), // special-cased below — God has ALL permissions
};

export function hasPermission(
  roles: string[],
  permission: Permission,
): boolean {
  return roles.some(
    (r) => r === ROLE.GOD || (ROLE_PERMISSIONS[r as Role]?.has(permission) ?? false),
  );
}

/** True if the user has the God role (used for routes/UI that must not use `hasPermission`). */
export function isGodRole(roles: string[]): boolean {
  return roles.includes(ROLE.GOD);
}

/**
 * True if the user administers intake for the whole roster — i.e. picks any
 * radiologist to view rather than doing their own onboarding. God and admin
 * qualify; a plain radiologist does not.
 */
export function isIntakeManager(roles: string[]): boolean {
  return roles.includes(ROLE.GOD) || roles.includes(ROLE.ADMIN);
}

export function usePermission(permission: Permission): boolean {
  const roles = useAuthStore((s) => s.roles);
  return hasPermission(roles, permission);
}
